import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useWebSocket } from "./WebSocketContext";
import { useAuth } from "./AuthContext";
import { showSuccess, showError } from "../utils/toastUtils";
import AppConstants from "../constants/appConstants";
import Configs from "../utils/Configs";
import CallsApi from "../api_calls/CallsApi";

const CallContext = createContext();
export const useCall = () => useContext(CallContext);

/**
 * Minimal, non-invasive fix: ensure we always stop media tracks when calls end
 * or when the provider unmounts. All original logic is preserved.
 */

const CallProvider = ({ children }) => {
  const { user } = useAuth();
  const { sendMessage, registerHandler, isConnected } = useWebSocket();

  const [isCalling, setIsCalling] = useState(false);
  const [isReceivingCall, setIsReceivingCall] = useState(false);
  const [isInCall, setIsInCall] = useState(false);
  const [callRejected, setCallRejected] = useState(false);
  const [callAccepted, setCallAccepted] = useState(false);
  const [callEnded, setCallEnded] = useState(false);

  const [isReconnecting, setIsReconnecting] = useState(false); // NEW STATE

  const [incomingCall, setIncomingCall] = useState(null);
  const [callType, setCallType] = useState(null);
  const [outgoingCallRequest, setOutgoingCallRequest] = useState(null);

  const userIdRef = useRef(null);
  // The other party's user_id and whether we placed the call. Kept in refs
  // (not derived from state) because timers, RTCPeerConnection callbacks and
  // WebSocket handlers outlive the render they were created in.
  const remotePeerIdRef = useRef(null);
  const isCallerRef = useRef(false);
  // Latest versions of the functions/state those long-lived callbacks need;
  // refreshed every render (see below the function definitions).
  const latest = useRef({});
  const peerConnectionRef = useRef(null);
  const candidateBufferRef = useRef([]);
  const callIdRef = useRef(null);
  const callTimeoutRef = useRef(null);
  const reconnectionTimeoutRef = useRef(null); // NEW REF

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  const remoteStreamBufferRef = useRef(null);

  // Refs to keep latest stream values available for unmount cleanup
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  useEffect(() => {
    remoteStreamRef.current = remoteStream;
  }, [remoteStream]);

  const incomingCallRef = useRef(null);
  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  // Set when a pending call was recovered with an "answer" intent (tapped
  // directly on the push notification with the app fully closed) — accepted
  // as soon as the resulting `incomingCall` state actually commits AND the
  // WebSocket is open (a cold start races the socket connecting against
  // this fetch; sending the answer before it's open would silently drop it).
  const pendingAutoAcceptRef = useRef(false);
  useEffect(() => {
    if (
      pendingAutoAcceptRef.current &&
      incomingCall &&
      isConnected &&
      !isInCall &&
      !callAccepted
    ) {
      pendingAutoAcceptRef.current = false;
      acceptCall();
    }
  }, [incomingCall, isConnected, isInCall, callAccepted]);

  useEffect(() => {
    console.log("[CallProvider] remoteStream state updated:", remoteStream);
    if (remoteStream) {
      console.log(
        "[CallProvider] remoteStream tracks:",
        remoteStream.getTracks()
      );
    }
  }, [remoteStream]);

  useEffect(() => {
    if (user?.user_id) {
      userIdRef.current = user.user_id;
    }
  }, [user]);

  const iceServers = {
    iceServers: [
      // Google STUN servers (fast & globally distributed)
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },

      // TURN server (UDP + TCP for reliability)
      {
        urls: `turn:${Configs.turnServer}?transport=udp`,
        username: Configs.turnUsername,
        credential: Configs.turnCredential,
      },
      {
        urls: `turn:${Configs.turnServer}?transport=tcp`,
        username: Configs.turnUsername,
        credential: Configs.turnCredential,
      },
    ],
  };

  // -------------------------
  // Helper: stop media tracks safely
  // -------------------------
  const stopStreamTracks = (stream) => {
    if (!stream) return;
    try {
      const tracks = stream.getTracks();
      if (!tracks || tracks.length === 0) return;
      tracks.forEach((track) => {
        try {
          track.stop();
        } catch (err) {
          console.warn("[STREAM] Error stopping track:", err);
        }
      });
    } catch (err) {
      console.warn("[STREAM] Failed to stop stream tracks:", err);
    }
  };

  const flushBufferedCandidates = async () => {
    const pc = peerConnectionRef.current;
    if (!pc || pc.signalingState !== "stable") {
      console.log(
        "[ICE] Cannot flush candidates. Signaling state is not stable."
      );
      return;
    }
    console.log("[ICE] Flushing buffered candidates...");
    for (const candidate of candidateBufferRef.current) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (err) {
        console.warn("[ICE] Error flushing candidate:", err);
      }
    }
    candidateBufferRef.current = [];
    console.log("[ICE] Flushed buffered candidates.");
  };

  const attachLocalMedia = async (isVoiceOnly = false) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: !isVoiceOnly,
        audio: true,
      });

      setLocalStream(stream);

      const pc = peerConnectionRef.current;
      if (!pc) {
        return stream;
      }

      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });
      console.log("[STREAM] Local media tracks attached to peer connection.");
      return stream;
    } catch (err) {
      console.error("[STREAM] Failed to get local media:", err);
      showError("Failed to get camera/mic access. Please check permissions.");
      endCall();
      return null;
    }
  };

  const prioritizeVP8 = (pc) => {
    if (!pc) return;
    const transceivers = pc.getTransceivers();
    const videoTransceiver = transceivers.find(
      (t) => t.receiver.track.kind === "video"
    );

    if (videoTransceiver) {
      const codecs = RTCRtpSender.getCapabilities("video").codecs;
      const vp8Codec = codecs.find((c) => c.mimeType === "video/VP8");

      if (vp8Codec) {
        const preferredCodecs = [
          vp8Codec,
          ...codecs.filter((c) => c.mimeType !== "video/VP8"),
        ];
        videoTransceiver.setCodecPreferences(preferredCodecs);
        console.log("[CODEC] VP8 prioritized for video transceiver.");
      }
    }
  };

  const restartIce = async () => {
    const pc = peerConnectionRef.current;
    const remoteUserId = remotePeerIdRef.current;

    // Only the caller re-offers. If both sides restarted at once their
    // offers would collide (glare) and the call would drop; the callee just
    // waits for the caller's restart offer instead.
    if (!isCallerRef.current) {
      console.log("[ICE-RESTART] Callee side: waiting for caller's restart offer.");
      return;
    }

    if (!pc || !remoteUserId || pc.signalingState !== "stable") {
      console.warn("[ICE-RESTART] Conditions not met for restart. Aborting.");
      setIsReconnecting(false);
      return;
    }

    try {
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);

      sendMessage({
        tag: "offer",
        payload: {
          from_user_id: user.user_id,
          recipient: remoteUserId,
          sdp: offer,
          callType: callType,
          callId: callIdRef.current,
          // Tells the backend this is mid-call renegotiation, not a new
          // call: no push notification / pending-call buffering.
          isRestart: true,
        },
      });
      console.log("[ICE-RESTART] Sending new offer with ICE restart flag.");
    } catch (err) {
      console.error("[ICE-RESTART] Failed to create ICE restart offer:", err);
      setIsReconnecting(false);
      latest.current.endCall();
    }
  };

  const setupPeerConnection = (recipientId) => {
    if (peerConnectionRef.current) {
      console.warn(
        "[PEER] Peer connection already exists. Closing existing one."
      );
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    console.log("[PEER] Setting up new RTCPeerConnection.");
    const pc = new RTCPeerConnection(iceServers);
    peerConnectionRef.current = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateType = event.candidate.candidate.split(" ")[7];
        console.log(
          `[ICE-LOG] Local ICE candidate generated: type=${candidateType}`
        );
      }

      if (event.candidate && userIdRef.current && recipientId) {
        const messagePayload = {
          tag: "ice-candidate",
          payload: {
            candidate: {
              candidate: event.candidate.candidate,
              sdpMid: event.candidate.sdpMid,
              sdpMLineIndex: event.candidate.sdpMLineIndex,
            },
            from_user_id: userIdRef.current,
            to_user_id: recipientId,
          },
        };

        sendMessage(messagePayload);
        console.log("[ICE-LOG] Message sent to backend:", messagePayload);
      }
    };

    pc.ontrack = (event) => {
      console.log(
        `[STREAM] Received remote track of kind: ${event.track.kind}`
      );
      remoteStreamBufferRef.current = event.streams[0];
      console.log("[STREAM] Remote stream buffered.");
    };

    pc.oniceconnectionstatechange = () => {
      console.log(
        `[ICE-STATE] ICE connection state changed to: ${pc.iceConnectionState}`
      );
      if (pc.iceConnectionState === "disconnected") {
        console.warn("[ICE-STATE] ICE disconnected. Attempting to restart...");
        if (!latest.current.isReconnecting) {
          setIsReconnecting(true);
          latest.current.restartIce();
        }
        if (reconnectionTimeoutRef.current) {
          clearTimeout(reconnectionTimeoutRef.current);
        }
        reconnectionTimeoutRef.current = setTimeout(() => {
          if (
            peerConnectionRef.current?.iceConnectionState === "disconnected"
          ) {
            console.error(
              "[ICE-STATE] Reconnection failed after timeout. Ending call."
            );
            latest.current.endCall();
          }
        }, 8000);
      } else if (
        pc.iceConnectionState === "connected" ||
        pc.iceConnectionState === "completed"
      ) {
        if (reconnectionTimeoutRef.current) {
          clearTimeout(reconnectionTimeoutRef.current);
          reconnectionTimeoutRef.current = null;
        }
        if (latest.current.isReconnecting) {
          setIsReconnecting(false);
          console.log("[ICE-STATE] Connection re-established.");
          showSuccess("Connection re-established!");
        }
      } else if (pc.iceConnectionState === "failed") {
        console.error("[ICE-STATE] ICE connection failed. Ending call.");
        setIsReconnecting(false);
        latest.current.endCall();
      }
    };

    pc.onsignalingstatechange = () => {
      console.log(
        `[SIGNALING-STATE] Signaling state changed to: ${pc.signalingState}`
      );
    };

    return pc;
  };

  const resetCallState = () => {
    stopStreamTracks(localStream);
    stopStreamTracks(remoteStream);

    setIsCalling(false);
    setIsReceivingCall(false);
    setIsInCall(false);
    setCallRejected(false);
    setCallAccepted(false);
    setCallEnded(false);
    setIncomingCall(null);
    setOutgoingCallRequest(null);
    setCallType(null);
    candidateBufferRef.current = [];
    setLocalStream(null);
    setRemoteStream(null);
    remoteStreamBufferRef.current = null;
    setIsReconnecting(false);
    callIdRef.current = null;
    remotePeerIdRef.current = null;
    isCallerRef.current = false;
  };

  const startCall = async (toUserId, type) => {
    if (!user?.user_id || !toUserId) return;
    remotePeerIdRef.current = toUserId;
    isCallerRef.current = true;
    setIsCalling(true);
    setCallType(type);
    callIdRef.current =
      window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;

    const pc = setupPeerConnection(toUserId);
    const stream = await attachLocalMedia(type === "voice");

    if (!stream) {
      console.error("[CALLER] Failed to get stream. Aborting call.");
      return;
    }

    try {
      prioritizeVP8(pc);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      console.log("[SDP-LOG] Created and setting local offer.");

      sendMessage({
        tag: "offer",
        payload: {
          from_user_id: user.user_id,
          recipient: toUserId,
          sdp: offer,
          callType: type,
          callId: callIdRef.current,
        },
      });
      // Unanswered: hang up (and tell the callee to stop ringing). Must use
      // the latest endCall — this render's copy predates the call state.
      callTimeoutRef.current = setTimeout(
        () => latest.current.endCall(),
        AppConstants.CALL_TIMEOUT_MS,
      );
    } catch (err) {
      console.error("[CALLER] Error creating offer:", err);
      endCall();
    }
  };

  const acceptCall = async () => {
    pendingAutoAcceptRef.current = false;
    const { fromUserId, sdp } = incomingCall || {};
    if (!sdp) {
      console.error("[RECEIVER] No SDP in incoming call. Cannot accept.");
      return;
    }

    try {
      const pc = setupPeerConnection(fromUserId);
      if (!pc) {
        console.error("[RECEIVER] Failed to create peer connection. Aborting.");
        return;
      }

      console.log("[SDP-LOG] Setting remote offer.");
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));

      const stream = await navigator.mediaDevices.getUserMedia({
        video: callType !== "voice",
        audio: true,
      });

      setLocalStream(stream);

      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });
      console.log("[PEER-ACCEPT] Local tracks attached.");

      prioritizeVP8(pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log("[SDP-LOG] Created and setting local answer.");

      sendMessage({
        tag: "answer",
        payload: {
          sdp: answer,
          from_user_id: user.user_id,
          to_user_id: fromUserId,
        },
      });

      flushBufferedCandidates();

      setIsReceivingCall(false);
      setIsInCall(true);
      setCallAccepted(true);

      if (remoteStreamBufferRef.current) {
        setRemoteStream(remoteStreamBufferRef.current);
      }
    } catch (error) {
      console.error("[RECEIVER] Error accepting call:", error);
      endCall();
    }
  };

  const handleAcceptCall = async (payload) => {
    const pc = peerConnectionRef.current;
    if (!pc) {
      console.error(
        "[CALLER] Peer connection not found. Cannot handle answer."
      );
      return;
    }
    if (pc.signalingState === "have-local-offer") {
      try {
        console.log("[SDP-LOG] Setting remote answer.");
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        flushBufferedCandidates();

        setIsCalling(false);
        setIsInCall(true);
        setCallAccepted(true);

        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }

        if (remoteStreamBufferRef.current) {
          setRemoteStream(remoteStreamBufferRef.current);
        }
      } catch (err) {
        console.error("[CALLER] CRITICAL ERROR: Failed to handle answer:", err);
        endCall();
      }
    }
  };

  const handleIncomingCall = (payload) => {
    const isRestartOffer =
      payload?.isRestart ||
      (isInCall && payload?.sender_id === remotePeerIdRef.current);

    if (isRestartOffer && payload?.sdp?.type === "offer") {
      console.log(
        "[RE-OFFER] Received re-offer. Handling as part of ICE restart."
      );
      const pc = peerConnectionRef.current;
      if (pc) {
        pc.setRemoteDescription(new RTCSessionDescription(payload.sdp))
          .then(() => pc.createAnswer())
          .then((answer) => pc.setLocalDescription(answer))
          .then(() => {
            sendMessage({
              tag: "answer",
              payload: {
                sdp: pc.localDescription,
                from_user_id: user.user_id,
                to_user_id: payload.sender_id,
              },
            });
            flushBufferedCandidates();
            setIsReconnecting(false);
            showSuccess("Connection re-established!");
          })
          .catch((err) => {
            console.error("[RE-OFFER] Failed to handle re-offer:", err);
            setIsReconnecting(false);
            endCall();
          });
      }
      return;
    }
    // Already ringing or in a call with someone else: the new caller just
    // times out on their side rather than hijacking the current call.
    if (isInCall || isReceivingCall || isCalling) {
      console.log("[CALL] Busy — ignoring offer from", payload?.sender_id);
      return;
    }
    remotePeerIdRef.current = payload.sender_id;
    isCallerRef.current = false;
    callIdRef.current = payload.callId || null;
    setIncomingCall({
      fromUserId: payload.sender_id,
      senderName: payload.sender_name,
      avatarUrl: payload.avatar_url,
      sdp: payload.sdp,
      callId: payload.callId,
    });
    setCallType(payload.callType);
    setIsReceivingCall(true);
  };

  const handleRemoteICECandidate = async (payload) => {
    const candidate = new RTCIceCandidate(payload.candidate);
    console.log("[ICE-LOG] Remote ICE candidate received.");
    const pc = peerConnectionRef.current;

    if (
      pc &&
      pc.remoteDescription &&
      (pc.signalingState === "stable" ||
        pc.signalingState === "have-local-offer")
    ) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (err) {
        console.warn("[ICE] Failed to add candidate:", err);
      }
    } else {
      console.log("[ICE] Remote description not set. Buffering candidate.");
      candidateBufferRef.current.push(candidate);
    }
  };

  const handleCallRejected = () => {
    showError("Call was rejected.");
    setCallRejected(true);
    // The callee already knows the call is over (they rejected it) — don't re-signal them.
    endCall(false);
  };

  const handleCallEnded = () => {
    endCall(false);
  };

  const openVoiceCall = (contact) => {
    setOutgoingCallRequest({ contact, type: "voice" });
    startCall(contact.id, "voice");
  };

  const openCall = (contact) => {
    setOutgoingCallRequest({ contact, type: "video" });
    startCall(contact.id, "video");
  };

  const rejectCall = (fromUserId) => {
    pendingAutoAcceptRef.current = false;
    sendMessage({
      tag: "reject_call",
      payload: {
        from_user_id: user.user_id,
        to_user_id: fromUserId,
        callId: callIdRef.current,
      },
    });
    // reject_call already told the caller the call is over — don't also send call-ended.
    endCall(false);
  };

  const endCall = (shouldSignalRemote = true) => {
    // Give the "Call ended"/"Call rejected" screen a moment to be visible
    // before the call UI unmounts, instead of vanishing instantly.
    const delayReset = 2000;
    console.log("[END-CALL] Fired.", { shouldSignalRemote, delayReset });

    const remoteUserId =
      remotePeerIdRef.current ||
      incomingCall?.fromUserId ||
      outgoingCallRequest?.contact?.id;

    if (shouldSignalRemote && remoteUserId) {
      console.log(`[END-CALL] Signaling remote user (${remoteUserId})`);
      sendMessage({
        tag: "call-ended",
        payload: {
          from_user_id: user.user_id,
          to_user_id: remoteUserId,
          callId: callIdRef.current,
        },
      });
    }

    try {
      const localStream = localStreamRef.current;
      const remoteStream = remoteStreamRef.current;
      if (localStream) {
        localStream.getTracks().forEach((track) => {
          console.log(`[END-CALL] Stopping local track: ${track.kind}`);
          track.stop();
        });
      }
      setLocalStream(null);

      if (remoteStream) {
        remoteStream.getTracks().forEach((track) => {
          console.log(`[END-CALL] Stopping remote track: ${track.kind}`);
          track.stop();
        });
      }
      setRemoteStream(null);

      if (peerConnectionRef.current) {
        peerConnectionRef.current.getSenders().forEach((sender) => {
          try {
            sender.track?.stop();
          } catch (e) {}
        });
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }
      if (reconnectionTimeoutRef.current) {
        clearTimeout(reconnectionTimeoutRef.current);
        reconnectionTimeoutRef.current = null;
      }

      const videoEls = document.querySelectorAll("video");
      const audioEls = document.querySelectorAll("audio");
      [...videoEls, ...audioEls].forEach((el) => {
        if (el.srcObject) {
          el.srcObject = null;
        }
      });
    } catch (err) {
      console.error("[END-CALL] Cleanup error:", err);
    }

    setCallEnded(true);

    setTimeout(() => {
      console.log("[CALL] Call ended. State reset.");
      resetCallState();
    }, delayReset);
  };

  useEffect(() => {
    return () => {
      stopStreamTracks(localStreamRef.current);
      stopStreamTracks(remoteStreamRef.current);

      if (peerConnectionRef.current) {
        try {
          peerConnectionRef.current.close();
        } catch (err) {
          console.warn("[PEER] Error closing peer connection on unmount:", err);
        }
        peerConnectionRef.current = null;
      }

      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }
      if (reconnectionTimeoutRef.current) {
        clearTimeout(reconnectionTimeoutRef.current);
        reconnectionTimeoutRef.current = null;
      }
    };
  }, []);

  // Refreshed every render so long-lived callbacks never act on stale state.
  latest.current = {
    endCall,
    restartIce,
    resetCallState,
    handleIncomingCall,
    handleAcceptCall,
    handleRemoteICECandidate,
    handleCallRejected,
    handleCallEnded,
    isReconnecting,
  };

  useEffect(() => {
    // Registered once; each forwards to the latest handler (see `latest`).
    registerHandler("offer", (p) => latest.current.handleIncomingCall(p));
    registerHandler("answer", (p) => latest.current.handleAcceptCall(p));
    registerHandler("ice-candidate", (p) =>
      latest.current.handleRemoteICECandidate(p)
    );
    registerHandler("reject_call", () => latest.current.handleCallRejected());
    registerHandler("call-ended", () => latest.current.handleCallEnded());
    registerHandler("pong", () => {});
    registerHandler("general", (data) => {
      showSuccess("Action was successful.");
      if (data?.result?.data?.message === "block") {
        window.dispatchEvent(
          new CustomEvent("userBlocked", { detail: data.peer_id })
        );
      }
    });
    // Backend refused a paid action (call/chat/like) — subscription ran out.
    // Drop any call we were starting, then send them to renew.
    registerHandler("subscription_expired", () => {
      latest.current.resetCallState();
      showError("Your subscription has expired. Please renew to continue.");
      setTimeout(
        () => window.location.assign(AppConstants.RENEW_SUBSCRIPTION_PATH),
        AppConstants.TOAST_AUTO_DISMISS_MS,
      );
    });
    registerHandler("block", () => showSuccess("Action was successful."));
    registerHandler("unblock", () => showSuccess("Action was successful."));
    registerHandler("like", () => showSuccess("Action was successful."));
    registerHandler("unlike", () => showSuccess("Action was successful."));
    return () => {
      registerHandler("offer", null);
      registerHandler("answer", null);
      registerHandler("ice-candidate", null);
      registerHandler("reject_call", null);
      registerHandler("call-ended", null);
      registerHandler("pong", null);
      registerHandler("general", null);
      registerHandler("subscription_expired", null);
      registerHandler("block", null);
      registerHandler("unblock", null);
      registerHandler("like", null);
      registerHandler("unlike", null);
    };
  }, []);

  // Recover a call offer that arrived while the app was fully closed. The
  // WebRTC offer is only ever delivered over the live WebSocket, so if the
  // app wasn't running when it came in, the offer never reached this device
  // at all — the backend buffers it (PendingCall) precisely so a cold start
  // can fetch it here instead of the call simply vanishing.
  const pendingCallCheckedRef = useRef(false);
  useEffect(() => {
    if (!user?.user_id || pendingCallCheckedRef.current) return;
    pendingCallCheckedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const shouldAutoAnswer = params.get("callAction") === "answer";
    if (params.has("callAction")) {
      params.delete("callAction");
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (newSearch ? `?${newSearch}` : "")
      );
    }

    CallsApi.getPendingCall().then((pending) => {
      // A live "offer" may have arrived over the WebSocket in the meantime
      // (or this device is already busy) — don't clobber it.
      if (!pending || incomingCallRef.current || isInCall) return;

      callIdRef.current = pending.callId || null;
      remotePeerIdRef.current = pending.sender_id;
      isCallerRef.current = false;
      if (shouldAutoAnswer) pendingAutoAcceptRef.current = true;

      setIncomingCall({
        fromUserId: pending.sender_id,
        senderName: pending.sender_name,
        avatarUrl: pending.avatar_url,
        sdp: pending.sdp,
        callId: pending.callId,
      });
      setCallType(pending.callType);
      setIsReceivingCall(true);
    });
  }, [user?.user_id]);

  // Handle "Answer"/"Decline" actions tapped on a push notification while
  // the app is backgrounded (relayed here from service-worker.js).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleServiceWorkerMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.type !== "CALL_NOTIFICATION_ACTION") return;

      if (msg.action === "decline") {
        if (incomingCallRef.current) {
          rejectCall(incomingCallRef.current.fromUserId);
        }
        return;
      }

      if (msg.action === "answer") {
        if (incomingCallRef.current) {
          acceptCall();
        } else {
          // The offer may not have arrived over the WebSocket yet if the
          // tab was just resumed from the background — give it a moment.
          setTimeout(() => {
            if (incomingCallRef.current) acceptCall();
          }, 800);
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, []);

  return (
    <CallContext.Provider
      value={{
        callType,
        callAccepted,
        isCalling,
        isReceivingCall,
        isInCall,
        callRejected,
        callEnded,
        incomingCall,
        localStream,
        remoteStream,
        acceptCall,
        rejectCall,
        endCall,
        outgoingCallRequest,
        openCall,
        openVoiceCall,
        peerConnectionRef,
        isReconnecting,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export default CallProvider;
